'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { type MDXEditorMethods, type MDXEditorProps } from '@mdxeditor/editor';
import { useDebounceCallback, useTimeout } from 'usehooks-ts';
import { REPOST_OPTIMISTIC_PREPEND_VARIANTS } from '@/config/feed';
import { IMAGE_MAX_RAW_SIZE } from '@/config/images';
import {
  ARTICLE_COVER_MAX_FILES,
  ARTICLE_INLINE_SUPPORTED_MIME_TYPES,
  ARTICLE_SUPPORTED_ATTACHMENT_MIME_TYPES,
  ARTICLE_SUPPORTED_FILE_TYPES,
  ARTICLE_TITLE_MAX_CHARACTER_LENGTH,
  ATTACHMENT_MAX_OTHER_SIZE,
  POST_ATTACHMENT_MAX_FILES,
  POST_MAX_CHARACTER_LENGTH,
  POST_SUPPORTED_ATTACHMENT_MIME_TYPES,
  POST_SUPPORTED_FILE_TYPES,
} from '@/config/posts';
import { PostController } from '@/controllers/post/post';
import { useAttachmentsMetadata } from '@/hooks/useAttachmentsMetadata/useAttachmentsMetadata';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile/useCurrentUserProfile';
import { useEditAttachments } from '@/hooks/useEditAttachments/useEditAttachments';
import { useEmojiInsert } from '@/hooks/useEmojiInsert/useEmojiInsert';
import { useMentionAutocomplete } from '@/hooks/useMentionAutocomplete/useMentionAutocomplete';
import { getContentWithMention } from '@/hooks/useMentionAutocomplete/useMentionAutocomplete.utils';
import { usePost } from '@/hooks/usePost/usePost';
import { useUndoRepost } from '@/hooks/useUndoRepost/useUndoRepost';
import { Logger } from '@/libs/logger/logger';
import { parseArticleContent } from '@/libs/post/articleContent';
import { collectAttachmentRefIndexes } from '@/libs/post/articleInlineMedia';
import { isViewerExcludedWotStream } from '@/models/stream/post/postStream.types';
import { toast } from '@/molecules/Toaster/toast';
import { POST_INPUT_PLACEHOLDER, POST_INPUT_VARIANT } from '@/organisms/PostInput/PostInput.constants';
import { useTimelineFeedContext } from '@/organisms/Timeline/Feed/TimelineFeed/TimelineFeedContext';
import { postKindBelongsToStream } from '@/stores/home/home.utils';
import { useLocalFilesStore } from '@/stores/localFiles/localFiles.store';
import type { UsePostInputOptions, UsePostInputReturn } from './usePostInput.types';

/**
 * Hook that encapsulates all PostInput logic.
 *
 * Manages:
 * - Content, tags, attachments, article state (via usePost)
 * - Expand/collapse behavior
 * - Emoji picker integration
 * - Form submission (post or reply)
 * - Click outside detection for collapse
 * - Content change notifications to parent
 * - File drag and drop handling
 * - Mention autocomplete (@username and pubky ID patterns)
 * - Clipboard paste handling for file attachments
 */
type AttachmentRejectionReason = 'maxFiles' | 'unsupportedType' | 'imageTooLarge' | 'fileTooLarge';

/**
 * How long an article edit waits for the file rows of its inline attachments before the editor opens
 * anyway. The read normally settles from Dexie at once; the cap keeps a degraded Nexus (its retries
 * run for a minute) or a hung request from leaving the editor behind its skeleton.
 */
const EDIT_INLINE_MEDIA_MAX_WAIT_MS = 5000;

const MAX_IMAGE_SIZE_LABEL = `${Math.round(IMAGE_MAX_RAW_SIZE / (1024 * 1024))}MB`;
const MAX_OTHER_SIZE_LABEL = `${Math.round(ATTACHMENT_MAX_OTHER_SIZE / (1024 * 1024))}MB`;

interface AttachmentLimits {
  maxFiles: number;
  supportedFileTypes: string;
}

/** One toast line per rejection reason; counts stand in for the file names toasts never show. */
function formatAttachmentRejection(
  reason: AttachmentRejectionReason,
  count: number,
  { maxFiles, supportedFileTypes }: AttachmentLimits,
): string {
  switch (reason) {
    case 'maxFiles':
      return `Maximum ${maxFiles} files allowed. Some files were not added.`;
    case 'unsupportedType':
      return count === 1
        ? `Unsupported file type. Supported: ${supportedFileTypes}.`
        : `${count} files have an unsupported type. Supported: ${supportedFileTypes}.`;
    case 'imageTooLarge':
      return count === 1
        ? `Image exceeds the ${MAX_IMAGE_SIZE_LABEL} limit.`
        : `${count} images exceed the ${MAX_IMAGE_SIZE_LABEL} limit.`;
    case 'fileTooLarge':
      return count === 1
        ? `File exceeds the ${MAX_OTHER_SIZE_LABEL} limit.`
        : `${count} files exceed the ${MAX_OTHER_SIZE_LABEL} limit.`;
  }
}

export function usePostInput({
  active = true,
  variant,
  postId,
  originalPostId,
  editPostId,
  editLock,
  editAttachmentUris,
  editContent,
  editIsArticle,
  onSuccess,
  placeholder,
  successToastTitle,
  isCollectionShare = false,
  expanded = false,
  onContentChange,
  onArticleModeChange,
  hasExternalContent,
  keepInlineMedia,
}: UsePostInputOptions): UsePostInputReturn {
  const isLockAnnouncement = editLock != null;
  const lockContentKind = editLock?.contentKind;

  // State
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [isDragging, setIsDragging] = useState(false);
  // Caret of the composer textarea, or null until it is known. Mention detection
  // and insertion are anchored here, so a mention completes anywhere in the text
  // and the text after the caret survives (#1959)
  const [caret, setCaret] = useState<number | null>(null);

  // Refs
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const markdownEditorRef = useRef<MDXEditorMethods>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  // Hooks
  const { currentUserPubky, userDetails: currentUserDetails } = useCurrentUserProfile();
  const {
    content,
    setContent,
    tags,
    setTags,
    attachments,
    setAttachments,
    existingAttachments,
    setExistingAttachments,
    isArticle,
    setIsArticle,
    articleTitle,
    setArticleTitle,
    lockTitle,
    setLockTitle,
    restoreComposerDraft,
    reply,
    post,
    repost,
    edit,
    isSubmitting,
    inlineMedia: inlineMediaSession,
    serializeArticleForLock,
  } = usePost({ keepInlineMedia, active });
  const timelineFeed = useTimelineFeedContext();
  const { undoRepost } = useUndoRepost(isCollectionShare, { outlivesOwner: true });

  // Article edits show only the cover in the attachment strip — inline images
  // live in the body. The cover is attachments[0] unless the published body
  // references attachment:0 (slot-0 rule: slot 0 is inline, no cover).
  const articleEditBody =
    variant === POST_INPUT_VARIANT.EDIT && editIsArticle ? parseArticleContent(editContent)?.body : undefined;
  const editRefIndexes = articleEditBody === undefined ? undefined : collectAttachmentRefIndexes(articleEditBody);
  const editDisplayUris =
    editRefIndexes === undefined ? undefined : editRefIndexes.has(0) ? [] : (editAttachmentUris ?? []).slice(0, 1);
  // Original attachments the user is never shown (not the cover, not
  // referenced by the body at open) — carried through the edit untouched so
  // saving cannot delete files the user did not see and remove
  const editPreservedUris =
    editRefIndexes === undefined
      ? undefined
      : (editAttachmentUris ?? []).filter((_uri, index) => {
          const isCover = index === 0 && !editRefIndexes.has(0);
          return !isCover && !editRefIndexes.has(index);
        });

  // The inline attachments the body references at open. Their file rows type each one (the
  // markdown never says video or image), and the rich editor imports the body only once, so the
  // editor waits for them: `useEditAttachments` seeds the cover strip alone and never sees these.
  const editInlineUris = editRefIndexes
    ? (editAttachmentUris ?? []).filter((_uri, index) => editRefIndexes.has(index))
    : [];
  const { files: editInlineFiles, isLoading: isEditInlineMetadataLoading } = useAttachmentsMetadata({
    fileUris: editInlineUris,
  });
  // Past the cap the editor opens with whatever rows landed: a slot without one imports as an image,
  // exactly as it does when the read settles without a row
  const [editInlineWaitExpired, setEditInlineWaitExpired] = useState(false);
  useTimeout(
    () => setEditInlineWaitExpired(true),
    isEditInlineMetadataLoading && !editInlineWaitExpired ? EDIT_INLINE_MEDIA_MAX_WAIT_MS : null,
  );
  const isEditInlineMediaLoading = isEditInlineMetadataLoading && !editInlineWaitExpired;
  // Session uploads know their own type; an edited article's attachments are typed by their rows
  const inlineMedia = {
    upload: inlineMediaSession.upload,
    getPreviewUrl: inlineMediaSession.getPreviewUrl,
    getMediaType: (uri: string) =>
      inlineMediaSession.getMediaType(uri) ??
      editInlineFiles.find((file) => file.uri === uri.trim())?.content_type ??
      null,
    getMediaName: (uri: string) =>
      inlineMediaSession.getMediaName(uri) ?? editInlineFiles.find((file) => file.uri === uri.trim())?.name ?? null,
  };

  // Seed and resolve the post's current attachments for the edit composer
  const { seededUris: seededAttachmentUris } = useEditAttachments({
    enabled: variant === POST_INPUT_VARIANT.EDIT,
    postId: editPostId,
    uris: editAttachmentUris,
    displayUris: editDisplayUris,
    existingAttachments,
    setExistingAttachments,
  });

  // The caret a mention selection acts on: the live one when it is known, else
  // the end of the value (prefilled content, a programmatic set)
  const mentionCaret = caret ?? content.length;

  // Handle mention selection - writes pubky{userId} over the pattern at the caret
  const handleMentionSelect = useCallback(
    (userId: string) => {
      const textarea = textareaRef.current;
      const selectionCaret = textarea?.selectionStart ?? mentionCaret;
      const insertion = getContentWithMention(content, selectionCaret, userId);

      if (insertion.content.length <= POST_MAX_CHARACTER_LENGTH) {
        setContent(insertion.content);
        setCaret(insertion.caret);

        // A controlled value parks the caret at the end of the textarea; put it
        // back after the inserted mention so the user keeps typing where they were
        requestAnimationFrame(() => {
          const element = textareaRef.current;
          if (!element) return;
          element.focus();
          element.setSelectionRange(insertion.caret, insertion.caret);
        });
        return;
      }

      // Over the character limit: leave the content alone, just keep the caret in
      // the textarea for the next edit
      textarea?.focus();
    },
    [content, mentionCaret, setContent],
  );

  // Mention autocomplete
  const {
    users: mentionUsers,
    isOpen: mentionIsOpen,
    selectedIndex: mentionSelectedIndex,
    setSelectedIndex: setMentionSelectedIndex,
    handleKeyDown: mentionHandleKeyDown,
  } = useMentionAutocomplete({ content, caret: mentionCaret, onSelect: handleMentionSelect });

  /**
   * Track the composer caret. Arrow keys, Home/End and clicks move it without a
   * change event, and mention detection follows the caret (#1959)
   */
  const handleSelectionChange = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const target = e.currentTarget;
    setCaret(target.selectionStart ?? target.value.length);
  };

  // Notify parent of content changes
  useEffect(() => {
    onContentChange?.(content, tags, attachments, articleTitle, existingAttachments);
  }, [content, tags, attachments, articleTitle, existingAttachments, onContentChange]);

  // Notify parent of article mode changes
  useEffect(() => {
    onArticleModeChange?.(isArticle);
  }, [isArticle, onArticleModeChange]);

  // The title and body inputs run ahead of `articleTitle` and `content` by the debounce. Null once
  // the state has caught up.
  const pendingArticleTitleRef = useRef<string | null>(null);
  const pendingArticleBodyRef = useRef<string | null>(null);

  // Handle click outside to collapse (only when expanded prop is false)
  useEffect(() => {
    if (expanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Check if click is inside the container
      if (containerRef.current?.contains(target)) return;

      // Check if click is inside MDXEditor popup containers (portaled outside the container)
      const mdxEditorPopup = document.querySelector('.mdxeditor-popup-container');
      if (mdxEditorPopup?.contains(target)) return;

      // Check if click is inside a dialog (portaled outside the container, e.g., EmojiPickerDialog)
      const dialogContent = document.querySelector('[data-slot="dialog-content"]');
      if (dialogContent?.contains(target)) return;

      // The lock-title input renders just above the composer container but belongs to it: focusing it
      // must not collapse the composer. `closest` on the target (not a document query) so multiple
      // mounted composers cannot shadow each other.
      if (target instanceof Element && target.closest('[data-lock-title-input]')) return;

      // An empty composer is not always idle: the lock flow holds the draft outside it, and an
      // article's inputs run ahead of the state by the debounce (the first keystrokes of a new
      // article are pending, not empty)
      const isInProgress =
        Boolean((pendingArticleBodyRef.current ?? content).trim()) ||
        tags.length > 0 ||
        attachments.length > 0 ||
        Boolean((pendingArticleTitleRef.current ?? articleTitle).trim()) ||
        Boolean(hasExternalContent?.());
      if (!isInProgress) {
        setIsExpanded(false);
        setIsArticle(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [expanded, content, tags, attachments, setIsArticle, articleTitle, hasExternalContent]);

  // Handle expand on interaction
  const handleExpand = useCallback(() => {
    if (!isExpanded) {
      setIsExpanded(true);
    }
  }, [isExpanded]);

  // Autosize textarea height (Safari doesn't support `field-sizing: content` yet).
  // Never measure an empty textarea: in a stretched layout its scrollHeight can
  // equal the available container height and persist that height inline.
  useLayoutEffect(() => {
    if (isArticle) return;
    const textarea = textareaRef.current;
    if (!textarea) return;

    if (content.length === 0) {
      textarea.style.height = '1lh';
      return;
    }

    // Measure from a collapsed height so a stretched flex/grid measurement cannot
    // become the next explicit textarea height.
    textarea.style.height = '0px';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [content, isArticle, isExpanded]);

  const getLatestArticle = () => ({
    title: pendingArticleTitleRef.current ?? articleTitle,
    body: pendingArticleBodyRef.current ?? content,
  });

  // Leaving article mode means the composer was emptied (a publish, a lock capture, a reset); the
  // pending values went out with it, and a commit still on its timer must not write them back
  useEffect(() => {
    if (isArticle) return;
    pendingArticleTitleRef.current = null;
    pendingArticleBodyRef.current = null;
  }, [isArticle]);

  // Handle submit using reply, repost, post, or edit method from hook
  const handleSubmit = useCallback(async () => {
    if (isSubmitting) return;

    // Articles publish what the editor holds right now: `articleTitle` and `content` trail the inputs
    // by the debounce, and a publish read from them would drop an image inserted in the last half
    // second, then delete its upload as unreferenced.
    const latestArticle = isArticle
      ? { title: pendingArticleTitleRef.current ?? articleTitle, body: pendingArticleBodyRef.current ?? content }
      : undefined;
    if (latestArticle) {
      // The state catches up with the inputs here and the pending values are consumed: a debounce
      // commit that fires after the publish has emptied the composer finds nothing left to apply
      setArticleTitle(latestArticle.title);
      setContent(latestArticle.body);
      pendingArticleTitleRef.current = null;
      pendingArticleBodyRef.current = null;
    }
    const hasBody = Boolean((latestArticle?.body ?? content).trim());

    // For replies, posts, and edits, require content or attachments. For reposts, content is optional. Content and title is required for articles.
    const totalAttachments = attachments.length + existingAttachments.length;
    if (
      (variant !== POST_INPUT_VARIANT.REPOST && !hasBody && totalAttachments === 0) ||
      (isArticle && (!hasBody || !(latestArticle?.title ?? articleTitle).trim()))
    )
      return;

    // Maps a newly added File to a local-store attachment backed by a fresh object URL
    const newFileToLocalAttachment = (file: File) => {
      const url = URL.createObjectURL(file);
      const isImage = file.type.startsWith('image');
      return { type: file.type, name: file.name, urls: { main: url, feed: isImage ? url : undefined } };
    };

    // Wrapper that prepends to timeline and calls original onSuccess.
    // Article store seeding is owned by usePost: the entry must be
    // index-aligned with the full `[cover?, ...inline]` attachment list,
    // which only usePost knows. Seeding here would overwrite it.
    const handleSuccess = (createdPostId: string) => {
      if (variant === POST_INPUT_VARIANT.EDIT) {
        if (!isArticle) {
          // Replace the optimistic store entry with the resulting attachment set:
          // kept attachments reuse their already-resolved URLs (the store's
          // set-difference revoke keeps reused blob: URLs alive), new files get
          // fresh object URLs. If a kept attachment never resolved, clear the
          // entry instead and let the Dexie/CDN render path take over.
          const allKeptResolved = existingAttachments.every((attachment) => attachment.urls !== null);
          const merged = allKeptResolved
            ? [
                ...existingAttachments.map((attachment) => ({
                  type: attachment.type,
                  name: attachment.name,
                  urls: attachment.urls as { main: string; feed?: string; large?: string },
                })),
                ...attachments.map(newFileToLocalAttachment),
              ]
            : [];

          useLocalFilesStore.getState().setPostAttachments(createdPostId, merged);
        }
        onSuccess?.(createdPostId);
        return;
      }

      if (!isArticle && attachments.length) {
        useLocalFilesStore.getState().setPostAttachments(createdPostId, attachments.map(newFileToLocalAttachment));
      }

      if (variant === POST_INPUT_VARIANT.REPLY) {
        onSuccess?.(createdPostId);
        return;
      }

      const feedVariant = timelineFeed?.variant;
      const shouldPrepend =
        variant === POST_INPUT_VARIANT.POST ||
        (variant === POST_INPUT_VARIANT.REPOST &&
          feedVariant != null &&
          REPOST_OPTIMISTIC_PREPEND_VARIANTS.has(feedVariant));

      if (shouldPrepend) {
        void (async () => {
          try {
            const streamId = timelineFeed?.streamId;
            if (!streamId) {
              await timelineFeed?.prependPosts(createdPostId);
              return;
            }

            /*
              WoT-sourced streams ('My network', 'Tagged as') never contain the
              viewer's own posts (Nexus excludes them and the local create path
              never writes into them), so prepending would flash the post and
              lose it on the next stream reset (#2308). Unlike the kind gate
              below, this check is NOT mirrored in `NewPostsSection`: unread ids
              there come from polling the WoT stream itself, so they belong.
            */
            if (isViewerExcludedWotStream(streamId)) {
              return;
            }

            const details = await PostController.getDetails({ compositeId: createdPostId });
            if (!details?.kind || postKindBelongsToStream(details.kind, streamId)) {
              await timelineFeed.prependPosts(createdPostId);
            }
          } catch (error) {
            Logger.error('[usePostInput] Failed to prepend created post to timeline', {
              error,
              createdPostId,
              streamId: timelineFeed?.streamId,
            });
          } finally {
            setIsExpanded(false);
          }
        })();
      } else {
        setIsExpanded(false);
      }

      onSuccess?.(createdPostId);
    };

    switch (variant) {
      case POST_INPUT_VARIANT.REPLY:
        await reply({ postId: postId!, onSuccess: handleSuccess });
        break;
      case POST_INPUT_VARIANT.REPOST:
        await repost({
          originalPostId: originalPostId!,
          successToastTitle,
          onSuccess: handleSuccess,
          onUndo: undoRepost,
        });
        break;
      case POST_INPUT_VARIANT.EDIT:
        await edit({
          editPostId: editPostId!,
          isLockAnnouncement: isLockAnnouncement || undefined,
          lockContentKind,
          originalAttachmentUris: seededAttachmentUris,
          preservedAttachmentUris: editPreservedUris,
          article: latestArticle,
          onSuccess: handleSuccess,
        });
        break;
      case POST_INPUT_VARIANT.POST:
      default:
        await post({ article: latestArticle, onSuccess: handleSuccess });
        break;
    }
  }, [
    content,
    attachments,
    existingAttachments,
    isArticle,
    articleTitle,
    variant,
    postId,
    originalPostId,
    successToastTitle,
    reply,
    post,
    repost,
    edit,
    editPostId,
    isLockAnnouncement,
    lockContentKind,
    seededAttachmentUris,
    editPreservedUris,
    isSubmitting,
    setArticleTitle,
    setContent,
    onSuccess,
    timelineFeed,
    undoRepost,
  ]);

  // Handle textarea change with validation
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      if (value.length <= POST_MAX_CHARACTER_LENGTH) {
        setContent(value);
        // Typing moves the caret without a selection event; mention detection
        // needs it before the next render (#1959)
        setCaret(e.target.selectionStart ?? value.length);
      }
    },
    [setContent],
  );

  // Wrapper to apply validation when emoji is inserted
  const handleEmojiChange = useCallback(
    (newValue: string) => {
      if (newValue.length <= POST_MAX_CHARACTER_LENGTH) {
        setContent(newValue);
      }
    },
    [setContent],
  );

  // Only a commit of the value the input still holds lands: a timer carrying an older value, or one
  // that fires after a submit consumed the pending value, finds a mismatch and does nothing.
  const commitArticleTitle = useDebounceCallback((value: string) => {
    if (pendingArticleTitleRef.current !== value) return;
    pendingArticleTitleRef.current = null;
    setArticleTitle(value);
  }, 500);

  // Handle article title change with validation
  const handleArticleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length > ARTICLE_TITLE_MAX_CHARACTER_LENGTH) return;
    pendingArticleTitleRef.current = value;
    commitArticleTitle(value);
  };

  const commitArticleBody = useDebounceCallback((markdown: string) => {
    if (pendingArticleBodyRef.current !== markdown) return;
    pendingArticleBodyRef.current = null;
    setContent(markdown);
  }, 500);

  // Handle article body change - length validation is handled via MDXEditor's maxLength plugin.
  // Rich text and markdown mode both report here; the rich text editor knows nothing of markdown mode.
  const handleArticleBodyChange: NonNullable<MDXEditorProps['onChange']> = (markdown) => {
    pendingArticleBodyRef.current = markdown;
    commitArticleBody(markdown);
  };

  // Emoji insert handler
  const handleEmojiSelect = useEmojiInsert({
    inputRef: textareaRef,
    value: content,
    onChange: handleEmojiChange,
  });

  // File handling - shared logic for both file input and drag/drop
  const handleFilesAdded = useCallback(
    (files: File[]) => {
      if (isSubmitting || files.length === 0) return;

      // Articles cap the picker at the cover; inline body images are uploaded separately.
      const ATTACHMENT_MAX_FILES = isArticle ? ARTICLE_COVER_MAX_FILES : POST_ATTACHMENT_MAX_FILES;
      const SUPPORTED_ATTACHMENT_MIME_TYPES = isArticle
        ? ARTICLE_SUPPORTED_ATTACHMENT_MIME_TYPES
        : POST_SUPPORTED_ATTACHMENT_MIME_TYPES;
      const SUPPORTED_FILE_TYPES = isArticle ? ARTICLE_SUPPORTED_FILE_TYPES : POST_SUPPORTED_FILE_TYPES;

      const currentCount = attachments.length + existingAttachments.length;
      const availableSlots = ATTACHMENT_MAX_FILES - currentCount;

      if (availableSlots <= 0) {
        toast({
          variant: 'error',
          description: isArticle
            ? 'Articles support one cover image. Remove it first, or drop the image in the editor to add it inline.'
            : `Maximum ${ATTACHMENT_MAX_FILES} files allowed`,
        });
        return;
      }

      const validFiles: File[] = [];
      // Toasts never name files, so rejections are tallied per reason and the
      // count tells the user how many files were refused.
      const rejections = new Map<AttachmentRejectionReason, number>();
      const reject = (reason: AttachmentRejectionReason) => rejections.set(reason, (rejections.get(reason) ?? 0) + 1);

      for (const file of files) {
        if (validFiles.length >= availableSlots) {
          reject('maxFiles');
          break;
        }

        // Check against specific supported MIME types from pubky-app-specs
        const isAcceptedType = SUPPORTED_ATTACHMENT_MIME_TYPES.includes(file.type);
        if (!isAcceptedType) {
          reject('unsupportedType');
          continue;
        }

        const isImage = file.type.startsWith('image/');

        if (isImage && file.size > IMAGE_MAX_RAW_SIZE) {
          reject('imageTooLarge');
          continue;
        }

        if (!isImage && file.size > ATTACHMENT_MAX_OTHER_SIZE) {
          reject('fileTooLarge');
          continue;
        }

        validFiles.push(file);
      }

      if (rejections.size > 0) {
        toast({
          variant: 'error',
          description: [...rejections]
            .map(([reason, count]) =>
              formatAttachmentRejection(reason, count, {
                maxFiles: ATTACHMENT_MAX_FILES,
                supportedFileTypes: SUPPORTED_FILE_TYPES,
              }),
            )
            .join('\n'),
        });
      }

      if (validFiles.length > 0) {
        setAttachments((prev) => [...prev, ...validFiles]);
      }
    },
    [isArticle, isSubmitting, attachments.length, existingAttachments.length, setAttachments],
  );

  // Remove an existing attachment from the edit composer (removed from the post on submit)
  const removeExistingAttachment = (uri: string) => {
    setExistingAttachments((prev) => prev.filter((attachment) => attachment.uri !== uri));
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current += 1;

      // Only set dragging if there are files being dragged
      if (e.dataTransfer.types.includes('Files')) {
        setIsDragging(true);
        // Auto-expand when dragging files over
        if (!isExpanded) {
          setIsExpanded(true);
        }
      }
    },
    [isExpanded],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;

    // Only set isDragging to false when we've left all nested elements
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  // Stages media files and inserts their markdown at the rich-text editor's
  // caret. Fallback for drops Lexical ignores (see handleDrop). Every kind
  // shares the image syntax; the editor's import routes non-images by their
  // session type. The whole drop is staged in one tick so the session's batch
  // budget sees all of it; a refused file was already toasted by the handler
  // and inserts nothing.
  const insertInlineMediaAtCaret = async (files: File[]) => {
    const staged = await Promise.allSettled(files.map((file) => inlineMedia.upload(file)));
    const uris = staged.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []));
    if (uris.length === 0) return;
    try {
      markdownEditorRef.current?.focus();
      for (const uri of uris) markdownEditorRef.current?.insertMarkdown(`![](${uri})`);
    } catch (error) {
      // An insert the editor refused after the files were staged, which the author must hear about
      Logger.error('[usePostInput] Failed to insert dropped media', { error });
      toast({ variant: 'error', description: 'Could not insert the file. Try again.' });
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      const alreadyHandled = e.defaultPrevented;
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragging(false);

      // In article mode the body editors consume image drops themselves
      // (Lexical's DROP_COMMAND / the markdown textarea handler) and call
      // preventDefault before the event bubbles here. Drag state is still
      // reset above; only the cover-attachment handling is skipped.
      if (isArticle && alreadyHandled) return;

      const dataTransfer = e.dataTransfer;
      if (!dataTransfer) return;

      // Extract files from drag event
      const files: File[] = [];

      for (const item of dataTransfer.items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();

          if (file) {
            files.push(file);
          }
        }
      }

      // Drops landing on non-editable islands inside the rich-text editor
      // (an already-inserted image) are ignored by Lexical — no
      // preventDefault — so they'd fall through to the cover. The user aimed
      // at the editor: insert inline instead. Unsupported files fall through
      // to handleFilesAdded for its standard unsupported-type toast.
      if (isArticle && e.target instanceof Element && e.target.closest('.mdxeditor')) {
        const mediaFiles = files.filter((file) => ARTICLE_INLINE_SUPPORTED_MIME_TYPES.includes(file.type));
        if (mediaFiles.length > 0) {
          void insertInlineMediaAtCaret(mediaFiles);
          return;
        }
      }

      handleFilesAdded(files);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- insertInlineMediaAtCaret only uses stable refs and the upload handle
    [handleFilesAdded, isArticle, inlineMediaSession],
  );

  // Trigger file input click
  const handleFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Handle paste events - extract files from clipboard
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const files: File[] = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      // Only prevent default if we have files - allow normal text paste
      e.preventDefault();
      handleFilesAdded(files);
    }
  };

  const handleArticleClick = () => setIsArticle(true);

  // Derived values
  const hasContent = content.trim().length > 0;
  const displayPlaceholder = placeholder ?? POST_INPUT_PLACEHOLDER[variant];

  return {
    // Refs
    textareaRef,
    markdownEditorRef,
    containerRef,
    fileInputRef,

    // State
    content,
    setContent,
    tags,
    setTags,
    attachments,
    setAttachments,
    existingAttachments,
    setExistingAttachments,
    isArticle,
    setIsArticle,
    articleTitle,
    setArticleTitle,
    lockTitle,
    setLockTitle,
    restoreComposerDraft,
    isDragging,
    isExpanded,
    isSubmitting,
    showEmojiPicker,
    setShowEmojiPicker,
    inlineMedia,
    isEditInlineMediaLoading,
    serializeArticleForLock,
    getLatestArticle,

    // Mention autocomplete state
    mentionUsers,
    mentionIsOpen,
    mentionSelectedIndex,
    setMentionSelectedIndex,

    // Derived values
    hasContent,
    displayPlaceholder,
    currentUserPubky,
    currentUserDetails,

    // Handlers
    handleExpand,
    handleSubmit,
    handleChange,
    handleArticleClick,
    handleArticleTitleChange,
    handleArticleBodyChange,
    handleEmojiSelect,
    handleFilesAdded,
    handleFileClick,
    removeExistingAttachment,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handlePaste,
    handleSelectionChange,
    handleMentionSelect,
    handleMentionKeyDown: mentionHandleKeyDown,
  };
}
